import { useEffect, useRef, useState } from 'react';
import { payAPI } from '../../../../../services/redwolfSdkAPI';
import { addClasses } from '../../../../../utils/eventListeners';
import { useGoogleAnalytics } from '../../../../common/hooks/useGoogleAnalytics';
import useTechnicalErrorMessage from '../../../../common/hooks/useTechnicalErrorMessage';
import * as Sentry from '@sentry/react';
import Modal from '../../../../common/components/Modal';
import Spinner from '../../../../common/components/Spinner';
import { openNotification } from '../../../../../store/commonSlice';
import { notification } from '../../../../../utils/constants';
import { useDispatch } from 'react-redux';
import useRequestRedwolfService from '../../../../common/hooks/useRequestRedwolfService';
import { getToken } from '../../../../../utils/localStorage';
import useMobileView from '../../../../common/hooks/useMobileView';

interface updateCardProps {
	openUpdateCardModal: boolean;
	selectedCard: any;
	closeModal: () => void;
	getCreditCards: () => void;
	openDeleteCardModal: () => void;
}

const UpdateCard = ({
	openUpdateCardModal,
	selectedCard,
	closeModal,
	getCreditCards,
	openDeleteCardModal
}: updateCardProps) => {
	const [cardErrorMessages] = useState({
		expirationDateMessage: 'Invalid expiration date.',
		cvvMessage: 'CVV is incorrect',
		postalCode: 'ZIP Code does not match',
		cardHolderName: 'Please input valid name.',
		commonErrorMessage: 'Invalid card details. Try again.'
	});
	const [updateCreditCardForm, setUpdateCreditCardForm] = useState({
		cardNumber: '',
		cardholderName: '',
		cardExpiryDate: '',
		cvv: '',
		zipCode: '',
		expiryMonth: '',
		expiryYear: ''
	});
	const [invalidCardErrorMessages, setInvalidCardErrorMessages] = useState({
		cvvErrorMessageBorderVisible: false,
		cvvErrorMessageVisible: false,
		expiryDateErrorBorderVisible: false,
		expiryDateErrorMessageVisible: false,
		zipCodeErrorBorderVisible: false,
		zipCodeErrorMessageVisble: false
	});
	const [expirationDate, setExpirationDate] = useState(new Date());
	const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(true);
	const [isSpinnerLoading, setIsSpinnerLoading] = useState(false);
	const [isExpiryDateValidate, setIsExpiryDateValidate] = useState(true);
	const [isSetDefaultCard, setIsSetDefaultCard] = useState(selectedCard.isDefault);
	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const [logGoogleAnalyticsEvent] = useGoogleAnalytics();
	const [requestRedwolfService] = useRequestRedwolfService();
	const cardHolderNameRef = useRef<HTMLInputElement>(null);
	const expiryDateRef = useRef<HTMLInputElement>(null);
	const zipCodeRef = useRef<HTMLInputElement>(null);
	const cvvRef = useRef<HTMLInputElement>(null);
	const cvvLength =
		selectedCard.cardType === 'Visa' || selectedCard.cardType === 'Mastercard' ? 3 : 4;
	const [isCardHolderNameInlineErrorMessageVisible, setIsCardHolderNameInlineErrorMessageVisible] =
		useState(false);
	const [isCommonInlineErrorMessageVisible, setIsCommonInlineErrorMessageVisible] = useState(false);
	const [creditCardErrorMessages, setCreditCardErrorMessages] = useState('');
	const [isMobileView] = useMobileView();
	const dispatch = useDispatch();
	const token = getToken();

	const transaction = Sentry.startInactiveSpan({ name: 'Update Card' });

	useEffect(() => {
		const cardNumber =
			selectedCard.cardType === 'American Express'
				? `***********${selectedCard.lastFour}`
				: `************${selectedCard.lastFour}`;
		const expirationDateInArray: any[] = selectedCard.expirationDate.split('/');
		const expirationDateYear =
			expirationDateInArray[1].length > 2
				? expirationDateInArray[1].slice(2, 4)
				: expirationDateInArray[1];
		const expirationDate: string = expirationDateInArray[0] + '/' + expirationDateYear;

		const toDay = new Date();
		toDay.setFullYear(parseInt(expirationDateYear), parseInt(expirationDateInArray[0]) - 1);
		setExpirationDate(toDay);

		setUpdateCreditCardForm({
			...updateCreditCardForm,
			cardNumber: cardNumber,
			cardholderName: selectedCard.cardholderName,
			cardExpiryDate: selectedCard.expirationDate,
			zipCode: selectedCard.billingAddress.postalCode
		});
		addClasses();

		const [month, year] = expirationDate.split('/');
		const today = new Date();
		const expiredDate = new Date();

		if (parseInt(month)) {
			expiredDate.setMonth(parseInt(month) - 1);
		}
		const currentYearFirstTwoDigits = parseInt(today.getFullYear().toString().slice(0, 2));
		const currentYearLastTwoDigits = parseInt(today.getFullYear().toString().slice(2, 4));

		if (year) {
			if (parseInt(year) >= currentYearLastTwoDigits) {
				expiredDate.setFullYear(parseInt(currentYearFirstTwoDigits + '' + year));
			} else {
				expiredDate.setFullYear(parseInt(currentYearFirstTwoDigits + 10 + '' + year));
			}
		}

		setExpirationDate(expiredDate);

		if (
			parseInt(month) > 12 ||
			(year &&
				(expiredDate.getFullYear() > today.getFullYear() + 19 ||
					expiredDate.getFullYear() < today.getFullYear()))
		) {
			setIsExpiryDateValidate(false);
			setInvalidCardErrorMessages({
				...invalidCardErrorMessages,
				expiryDateErrorBorderVisible: true,
				expiryDateErrorMessageVisible: true
			});
		} else {
			if (
				year &&
				today.getFullYear() === expiredDate.getFullYear() &&
				expiredDate.getMonth() < today.getMonth()
			) {
				setIsExpiryDateValidate(false);
				setInvalidCardErrorMessages({
					...invalidCardErrorMessages,
					expiryDateErrorBorderVisible: true,
					expiryDateErrorMessageVisible: true
				});
			} else {
				if (expirationDate.length === 5) {
					setIsExpiryDateValidate(true);
					setInvalidCardErrorMessages({
						...invalidCardErrorMessages,
						expiryDateErrorBorderVisible: false,
						expiryDateErrorMessageVisible: false
					});
				}
			}
		}
		logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_UPDATE_CARD');

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedCard]);

	const validateFormError = (inputName: string, formData: any, event: any) => {
		if (inputName === 'cardholderName') {
			if (formData.cardholderName && !isNaN(formData.cardholderName)) {
				setIsCardHolderNameInlineErrorMessageVisible(true);
			} else {
				setIsCardHolderNameInlineErrorMessageVisible(false);
			}
		} else if (inputName === 'cardExpiryDate') {
			if (!formData.cardExpiryDate) {
				setInvalidCardErrorMessages({
					...invalidCardErrorMessages,
					expiryDateErrorBorderVisible: false,
					expiryDateErrorMessageVisible: false
				});
			} else {
				try {
					const [month, year] = formData.cardExpiryDate.split('/');

					const today = new Date();
					const expiredDate = new Date();

					if (parseInt(month)) {
						expiredDate.setMonth(parseInt(month) - 1);
					}

					const currentYearFirstTwoDigits = parseInt(today.getFullYear().toString().slice(0, 2));
					const currentYearLastTwoDigits = parseInt(today.getFullYear().toString().slice(2, 4));

					if (year) {
						if (parseInt(year) >= currentYearLastTwoDigits) {
							expiredDate.setFullYear(parseInt(currentYearFirstTwoDigits + '' + year));
						} else {
							expiredDate.setFullYear(parseInt(currentYearFirstTwoDigits + 10 + '' + year));
						}
					}

					setExpirationDate(expiredDate);

					if (
						parseInt(month) > 12 ||
						(year &&
							(expiredDate.getFullYear() > today.getFullYear() + 19 ||
								expiredDate.getFullYear() < today.getFullYear()))
					) {
						setIsExpiryDateValidate(false);
						setIsCommonInlineErrorMessageVisible(false);
						setInvalidCardErrorMessages({
							...invalidCardErrorMessages,
							expiryDateErrorBorderVisible: true,
							expiryDateErrorMessageVisible: true
						});
					} else {
						if (
							year &&
							today.getFullYear() === expiredDate.getFullYear() &&
							expiredDate.getMonth() < today.getMonth()
						) {
							setIsExpiryDateValidate(false);
							setInvalidCardErrorMessages({
								...invalidCardErrorMessages,
								expiryDateErrorBorderVisible: true,
								expiryDateErrorMessageVisible: true
							});
						} else {
							if (formData.cardExpiryDate.length === 5) {
								setIsExpiryDateValidate(true);
								if (
									(!invalidCardErrorMessages.cvvErrorMessageVisible &&
										invalidCardErrorMessages.cvvErrorMessageBorderVisible) ||
									(!invalidCardErrorMessages.zipCodeErrorMessageVisble &&
										invalidCardErrorMessages.zipCodeErrorBorderVisible)
								) {
									setIsCommonInlineErrorMessageVisible(true);
									setInvalidCardErrorMessages({
										...invalidCardErrorMessages,
										expiryDateErrorBorderVisible: false,
										expiryDateErrorMessageVisible: false
									});
								} else {
									setInvalidCardErrorMessages({
										...invalidCardErrorMessages,
										expiryDateErrorBorderVisible: false,
										expiryDateErrorMessageVisible: false
									});
								}
							}
						}
					}
				} catch (err: any) {
					setIsSpinnerLoading(false);
				}
			}
		} else if (inputName === 'cvv') {
			if (event.nativeEvent.inputType === 'deleteContentBackward') {
				if (formData.cvv.length === 0 || formData.cvv.length === cvvLength) {
					setInvalidCardErrorMessages({
						...invalidCardErrorMessages,
						cvvErrorMessageBorderVisible: false,
						cvvErrorMessageVisible: false
					});
				} else {
					setInvalidCardErrorMessages({
						...invalidCardErrorMessages,
						cvvErrorMessageBorderVisible: true,
						cvvErrorMessageVisible: true
					});
				}
			} else {
				if (formData.cvv.length === cvvLength) {
					setInvalidCardErrorMessages({
						...invalidCardErrorMessages,
						cvvErrorMessageBorderVisible: false,
						cvvErrorMessageVisible: false
					});
					if (
						!invalidCardErrorMessages.expiryDateErrorBorderVisible &&
						isCommonInlineErrorMessageVisible &&
						!invalidCardErrorMessages.zipCodeErrorBorderVisible
					) {
						setIsCommonInlineErrorMessageVisible(false);
						setInvalidCardErrorMessages({
							...invalidCardErrorMessages,
							cvvErrorMessageBorderVisible: false,
							cvvErrorMessageVisible: false
						});
					}
				}
			}
		} else if (inputName === 'zipCode') {
			if (event.nativeEvent.inputType === 'deleteContentBackward') {
				if (formData.zipCode.length === 0 || formData.zipCode.length === 5) {
					setInvalidCardErrorMessages({
						...invalidCardErrorMessages,
						zipCodeErrorBorderVisible: false,
						zipCodeErrorMessageVisble: false
					});
				} else {
					setInvalidCardErrorMessages({
						...invalidCardErrorMessages,
						zipCodeErrorBorderVisible: true,
						zipCodeErrorMessageVisble: true
					});
				}
			} else {
				if (formData.zipCode.length === 0 || formData.zipCode.length === 5) {
					setInvalidCardErrorMessages({
						...invalidCardErrorMessages,
						zipCodeErrorBorderVisible: false,
						zipCodeErrorMessageVisble: false
					});
					if (
						!invalidCardErrorMessages.expiryDateErrorBorderVisible &&
						isCommonInlineErrorMessageVisible &&
						!invalidCardErrorMessages.cvvErrorMessageBorderVisible
					) {
						setIsCommonInlineErrorMessageVisible(false);
						setInvalidCardErrorMessages({
							...invalidCardErrorMessages,
							zipCodeErrorBorderVisible: false,
							zipCodeErrorMessageVisble: false
						});
					}
				} else {
					setInvalidCardErrorMessages({
						...invalidCardErrorMessages,
						zipCodeErrorBorderVisible: true,
						zipCodeErrorMessageVisble: true
					});
				}
			}
		}
	};

	useEffect(() => {
		if (
			!isCardHolderNameInlineErrorMessageVisible &&
			!isCommonInlineErrorMessageVisible &&
			!invalidCardErrorMessages.expiryDateErrorMessageVisible &&
			!invalidCardErrorMessages.cvvErrorMessageVisible &&
			updateCreditCardForm.cvv.length === cvvLength &&
			updateCreditCardForm.zipCode.length >= 5 &&
			updateCreditCardForm.cardExpiryDate.length > 3
		) {
			setIsSubmitButtonDisabled(false);
		} else {
			setIsSubmitButtonDisabled(true);
		}
	}, [
		isCardHolderNameInlineErrorMessageVisible,
		isCommonInlineErrorMessageVisible,
		invalidCardErrorMessages.expiryDateErrorMessageVisible,
		invalidCardErrorMessages.cvvErrorMessageVisible,
		updateCreditCardForm.cvv.length,
		updateCreditCardForm.zipCode.length,
		updateCreditCardForm.cardExpiryDate.length,
		cvvLength
	]);

	const handleOnBlur = (event: any) => {
		const name = event.target.name;
		const formData = { ...updateCreditCardForm };
		if (name === 'cvv') {
			if (formData.cvv.length === cvvLength) {
				setInvalidCardErrorMessages({
					...invalidCardErrorMessages,
					cvvErrorMessageBorderVisible: false,
					cvvErrorMessageVisible: false
				});
			}
			if (
				!invalidCardErrorMessages.expiryDateErrorBorderVisible &&
				!invalidCardErrorMessages.cvvErrorMessageVisible &&
				!invalidCardErrorMessages.zipCodeErrorBorderVisible
			) {
				setIsCommonInlineErrorMessageVisible(false);
				setInvalidCardErrorMessages({
					...invalidCardErrorMessages,
					cvvErrorMessageBorderVisible: false,
					cvvErrorMessageVisible: false
				});
			}
		} else if (name === 'expiryDate') {
			if (isExpiryDateValidate) {
				if (
					invalidCardErrorMessages.cvvErrorMessageBorderVisible ||
					invalidCardErrorMessages.zipCodeErrorBorderVisible
				) {
					setInvalidCardErrorMessages({
						...invalidCardErrorMessages,
						expiryDateErrorBorderVisible: false,
						expiryDateErrorMessageVisible: false
					});
				} else {
					setIsCommonInlineErrorMessageVisible(false);
					setInvalidCardErrorMessages({
						...invalidCardErrorMessages,
						expiryDateErrorBorderVisible: false,
						expiryDateErrorMessageVisible: false
					});
				}
			} else {
				setIsCommonInlineErrorMessageVisible(false);
			}
		} else if (name === 'zipCode') {
			if (updateCreditCardForm.zipCode.length >= 5) {
				setInvalidCardErrorMessages({
					...invalidCardErrorMessages,
					zipCodeErrorBorderVisible: false,
					zipCodeErrorMessageVisble: false
				});
			}
			if (
				isCommonInlineErrorMessageVisible &&
				!invalidCardErrorMessages.cvvErrorMessageBorderVisible &&
				!invalidCardErrorMessages.expiryDateErrorBorderVisible
			) {
				setIsCommonInlineErrorMessageVisible(false);
				setInvalidCardErrorMessages({
					...invalidCardErrorMessages,
					zipCodeErrorBorderVisible: false,
					zipCodeErrorMessageVisble: false
				});
			}
		}
	};

	const isNumeric = (value: string): boolean => {
		const REG_EXP = /^[0-9]+$/i;
		return REG_EXP.test(value);
	};

	const handleInputChange = (event: any) => {
		setCreditCardErrorMessages('');
		const { value, name } = event.target;

		let formData = { ...updateCreditCardForm };

		if (name === 'cardholderName') {
			formData = { ...updateCreditCardForm, [name]: value };
		} else {
			if (!value) {
				formData = { ...updateCreditCardForm, [name]: '' };
			}
			if (isNumeric(value)) {
				formData = { ...updateCreditCardForm, [name]: value };
			}
		}
		validateFormError(name, formData, event);

		setUpdateCreditCardForm(formData);
	};

	const handleExpirationDateChange = (event: any) => {
		setCreditCardErrorMessages('');
		const { value } = event.target;

		const formData = { ...updateCreditCardForm, cardExpiryDate: value };

		// Backspace
		if (event.nativeEvent.inputType === 'deleteContentBackward') {
			if (formData.cardExpiryDate.length === 0) {
				formData.cardExpiryDate = '';
			} else if (formData.cardExpiryDate.length === 2) {
				if (/\/[0-9]/gi.test(formData.cardExpiryDate)) {
					formData.cardExpiryDate = formData.cardExpiryDate.slice(1, 2);
				} else if (
					parseInt(formData.cardExpiryDate) <= 9 &&
					!parseInt(formData.cardExpiryDate.slice(0, 1))
				) {
					formData.cardExpiryDate = '';
				} else {
					formData.cardExpiryDate = formData.cardExpiryDate.slice(0, 1);
				}
			} else if (formData.cardExpiryDate.length === 3) {
				setInvalidCardErrorMessages({
					...invalidCardErrorMessages,
					expiryDateErrorBorderVisible: true,
					expiryDateErrorMessageVisible: true
				});
				if (!formData.cardExpiryDate.includes('/')) {
					formData.cardExpiryDate =
						formData.cardExpiryDate.slice(0, 1) + formData.cardExpiryDate.slice(2, 3) + '/';
				}
			} else {
				if (!isNaN(formData.cardExpiryDate)) {
					formData.cardExpiryDate =
						formData.cardExpiryDate.slice(0, 1) +
						formData.cardExpiryDate.slice(2, 3) +
						'/' +
						formData.cardExpiryDate.slice(3, 4);
				}
			}
		} else {
			if (!isNumeric(event.nativeEvent.data)) {
				return;
			}

			if (formData.cardExpiryDate.length === 1) {
				if (
					parseInt(formData.cardExpiryDate) <= 9 &&
					parseInt(formData.cardExpiryDate) !== 1 &&
					parseInt(formData.cardExpiryDate) !== 0
				) {
					formData.cardExpiryDate = '0' + formData.cardExpiryDate + '/';
				}
			} else if (formData.cardExpiryDate.length === 2) {
				formData.cardExpiryDate = formData.cardExpiryDate + '/';
			} else if (
				formData.cardExpiryDate.length === 4 &&
				/^\d{1}\/[0-9]/gi.test(formData.cardExpiryDate)
			) {
				if (formData.cardExpiryDate.slice(0, 1) !== '1') {
					formData.cardExpiryDate =
						'0' + formData.cardExpiryDate.slice(0, 1) + '/' + formData.cardExpiryDate.slice(2, 4);
				}
			}
		}

		validateFormError('cardExpiryDate', formData, event);

		setUpdateCreditCardForm(formData);
	};

	const handleSubmit = (event: any) => {
		event.preventDefault();
		callUpdateCreditCardAPI();
	};

	const callUpdateCreditCardAPI = () => {
		setIsSpinnerLoading(true);
		setInvalidCardErrorMessages({
			...invalidCardErrorMessages,
			expiryDateErrorBorderVisible: false,
			expiryDateErrorMessageVisible: false
		});
		setInvalidCardErrorMessages({
			...invalidCardErrorMessages,
			cvvErrorMessageBorderVisible: false,
			cvvErrorMessageVisible: false
		});

		const updateCreditCardInfo = {
			billingAddress: {
				firstName: updateCreditCardForm.cardholderName,
				postalCode: updateCreditCardForm.zipCode
			},
			cvv: updateCreditCardForm.cvv,
			expirationMonth: expirationDate.getMonth() + 1,
			expirationYear: expirationDate.getFullYear()
		};

		logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_UPDATE_CC_DETAILS');

		requestRedwolfService(
			payAPI.updateCreditCard(token, selectedCard.cardToken, updateCreditCardInfo)
		)
			.then((updateCardResponse: any) => {
				if (updateCardResponse.constructor.$metadata$.simpleName === 'Success') {
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_CC_UPDATED');

					if (isSetDefaultCard) {
						requestRedwolfService(payAPI.setDefaultCreditCard(token, selectedCard.cardToken))
							.then((defaultCardResponse: any) => {
								handleUpdateCardSuccess(defaultCardResponse);
							})
							.catch((err: any) => {
								setIsSpinnerLoading(false);
								Sentry.captureException(err);
							});
					} else {
						handleUpdateCardSuccess(updateCardResponse);
					}
				} else {
					setIsSpinnerLoading(false);

					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_UPDATE_CC_ERROR', {
						error: updateCardResponse.response.detail
					});
					Sentry.captureException(new Error('updateCreditCard:' + updateCardResponse.response));
					if (
						updateCardResponse.response.detail === 'Gateway Rejected: avs' ||
						updateCardResponse.response.detail === 'Address postal code is invalid' ||
						updateCardResponse.response.detail.toLowerCase().includes('zip code')
					) {
						setInvalidCardErrorMessages({
							...invalidCardErrorMessages,
							zipCodeErrorBorderVisible: true,
							zipCodeErrorMessageVisble: true
						});
						if (zipCodeRef.current !== null) {
							zipCodeRef.current.focus();
						}
					} else if (updateCardResponse.response.detail === 'Gateway Rejected: cvv') {
						setIsCommonInlineErrorMessageVisible(true);
						setInvalidCardErrorMessages({
							...invalidCardErrorMessages,
							expiryDateErrorBorderVisible: true,
							cvvErrorMessageBorderVisible: true
						});
						if (cvvRef.current !== null) {
							cvvRef.current.focus();
						}
					} else if (updateCardResponse.response.detail === 'Gateway Rejected: avs_and_cvv') {
						setIsCommonInlineErrorMessageVisible(true);
						setInvalidCardErrorMessages({
							...invalidCardErrorMessages,
							zipCodeErrorBorderVisible: true,
							cvvErrorMessageBorderVisible: true,
							expiryDateErrorBorderVisible: true
						});
						if (expiryDateRef.current !== null) {
							expiryDateRef.current.focus();
						}
					} else {
						if (updateCardResponse.response.detail) {
							setCreditCardErrorMessages(updateCardResponse.response.detail);
						}
						setIsCommonInlineErrorMessageVisible(true);
						setInvalidCardErrorMessages({
							...invalidCardErrorMessages,
							cvvErrorMessageBorderVisible: true,
							expiryDateErrorBorderVisible: true
						});
						if (expiryDateRef.current !== null) {
							expiryDateRef.current.focus();
						}
					}
				}
				transaction && transaction.end();
			})
			.catch((err: any) => {
				setIsSpinnerLoading(false);
				transaction && transaction.end();
				Sentry.captureException(err);
				logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_UPDATE_CC_ERROR', { error: err.message });
			});
	};

	const handleUpdateCardSuccess = (cardResponse: any) => {
		if (cardResponse.constructor.$metadata$.simpleName === 'Success') {
			setIsSpinnerLoading(false);
			closeModal();

			getCreditCards();

			dispatch(
				openNotification({
					type: 'SUCCESS',
					heading: 'Success',
					body: notification.message.updateCreditCardSuccess
				})
			);
		} else {
			setIsSpinnerLoading(false);
			showTechnicalErrorMessage();
		}
	};

	const closeUpdateCardModal = () => {
		closeModal();
		openDeleteCardModal();
	};

	return (
		<>
			{/* Update card Modal Popup Start */}
			<Modal
				containerId="updateCard"
				show={openUpdateCardModal}
				onHide={closeModal}
				heading="Update Card"
				overlaycontainerClass="cc-popup updateCard">
				<form className="edit-modal-form" onSubmit={handleSubmit}>
					<div className="pay-icons payment-card-icons">
						<span className="txt">
							VIZIO Account accepts:
							<span className="cc-icons">
								<span className="cc-visa">
									<img src="images/visa-logo.png" alt="visa card" />
								</span>
								<span className="cc-master">
									<img src="images/mastercard-logo.svg" alt="Master card" />
								</span>
								<span className="cc-ae">
									<img src="images/american-express-logo.png" alt="American Express card" />
								</span>
							</span>
						</span>
					</div>

					<div className="field">
						<div className="input-box form-group input-label">
							<input
								id="updateCardNumber"
								type="text"
								className="form-control active"
								autoComplete="cc-number"
								value={updateCreditCardForm.cardNumber}
								readOnly
							/>
							<label htmlFor="updateCardNumber" className="control-label">
								Credit Card Number
							</label>
						</div>
					</div>

					<div className="field">
						<div
							className={`input-box form-group input-label cc-details ${
								isCardHolderNameInlineErrorMessageVisible && 'invalid'
							}`}>
							<input
								id="credit-card-name"
								name="cardholderName"
								type="text"
								className="form-control active"
								aria-describedby="credit-card-name-error"
								aria-required="true"
								required
								aria-invalid="false"
								ref={cardHolderNameRef}
								value={updateCreditCardForm.cardholderName}
								onChange={handleInputChange}
								spellCheck="false"
							/>
							<label htmlFor="credit-card-name" className="control-label">
								Name on Card
							</label>
							<div
								className="invalid-feedback-item"
								id="credit-card-name-error"
								style={{ display: 'block' }}>
								{isCardHolderNameInlineErrorMessageVisible ? cardErrorMessages.cardHolderName : ''}
							</div>
						</div>
					</div>

					<div className="cc-fields">
						<div className="exp-cvv">
							<div
								className={`input-box form-group input-label cc-exp cc-details ${
									invalidCardErrorMessages.expiryDateErrorBorderVisible && 'invalid'
								}`}>
								<input
									id="credit-card-expiration"
									name="expiryDate"
									type="text"
									className="form-control active"
									aria-describedby="credit-card-expiration-error"
									aria-required="true"
									required
									aria-invalid="false"
									maxLength={5}
									value={updateCreditCardForm.cardExpiryDate}
									onChange={handleExpirationDateChange}
									onBlur={handleOnBlur}
									ref={expiryDateRef}
									spellCheck="false"
								/>
								<label htmlFor="credit-card-expiration" className="control-label">
									EXP. (MM/YY)
								</label>
								{isCommonInlineErrorMessageVisible ? (
									<div
										className="invalid-feedback-item"
										id="credit-card-expiration-error"
										style={{ display: 'block' }}>
										<span>{cardErrorMessages.commonErrorMessage}</span>
									</div>
								) : (
									<div className="invalid-feedback-item" role="alert" style={{ display: 'block' }}>
										<span>
											{invalidCardErrorMessages.expiryDateErrorMessageVisible
												? cardErrorMessages.expirationDateMessage
												: ''}
										</span>
									</div>
								)}
							</div>

							<div
								className={`input-box form-group input-label cc-cvc cc-details ${
									invalidCardErrorMessages.cvvErrorMessageBorderVisible ? 'invalid' : ''
								} ${updateCreditCardForm.cvv === '' && isSetDefaultCard ? 'cvv-focus' : ''}`}>
								<input
									id="credit-card-cvv"
									name="cvv"
									type="text"
									className="form-control"
									aria-describedby="credit-card-cvv-error"
									aria-required="true"
									required
									aria-invalid="false"
									value={updateCreditCardForm.cvv}
									maxLength={
										selectedCard.cardType === 'Visa' || selectedCard.cardType === 'Mastercard'
											? 3
											: 4
									}
									onChange={handleInputChange}
									onBlur={handleOnBlur}
									ref={cvvRef}
								/>
								<label htmlFor="credit-card-cvv" className="control-label">
									CVV
								</label>
								<div
									className="invalid-feedback-item"
									id="credit-card-cvv-error"
									style={{ display: 'block' }}>
									{invalidCardErrorMessages.cvvErrorMessageVisible
										? cardErrorMessages.cvvMessage
										: updateCreditCardForm.cvv === '' && isSetDefaultCard
										? 'CVV Required'
										: ''}
								</div>
							</div>
						</div>

						<div
							className={`input-box form-group input-label cc-zip cc-details ${
								invalidCardErrorMessages.zipCodeErrorBorderVisible && 'invalid'
							}`}>
							<input
								id="credit-card-zipcode"
								name="zipCode"
								type="text"
								className="form-control active"
								aria-describedby="credit-card-zipcode-error"
								aria-required="true"
								required
								aria-invalid="false"
								maxLength={5}
								value={updateCreditCardForm.zipCode}
								onChange={handleInputChange}
								onBlur={handleOnBlur}
								ref={zipCodeRef}
								spellCheck="false"
							/>
							<label htmlFor="credit-card-zipcode" className="control-label">
								ZIP Code
							</label>
							<div
								className="invalid-feedback-item"
								id="credit-card-zipcode-error"
								style={{ display: 'block' }}>
								{invalidCardErrorMessages.zipCodeErrorMessageVisble
									? cardErrorMessages.postalCode
									: ''}
							</div>
						</div>
					</div>
					<div className="invalid-feedback-item" id="credit-card-zipcode-error">
						{creditCardErrorMessages ? creditCardErrorMessages : ''}
					</div>

					<div className="braintree-info">
						<p>Payment will be processed by external vendor, Braintree - A PayPal Service</p>
					</div>

					<div className="button-field-group">
						<div className="form-checkbox CheckBox default-checkbox">
							<input
								type="checkbox"
								id="setAsDefault"
								checked={isSetDefaultCard}
								onChange={(event) => setIsSetDefaultCard(event.target.checked)}
								disabled={selectedCard.isDefault && true}
							/>
							<label htmlFor="setAsDefault">Set as Default</label>
						</div>

						{!isMobileView && (
							<button
								type="submit"
								className="secondary-button deleteCardBtn desktop-btn"
								onClick={closeUpdateCardModal}>
								Delete Card
							</button>
						)}
						<button
							type="submit"
							className="primary-button updateCardBtn"
							disabled={isSubmitButtonDisabled}>
							Save
						</button>
						{isMobileView && (
							<button
								type="submit"
								className="secondary-button deleteCardBtn mobile-btn"
								onClick={closeUpdateCardModal}>
								Delete Card
							</button>
						)}
					</div>
				</form>
			</Modal>
			{/* Update card Modal Popup end */}

			{isSpinnerLoading && <Spinner />}
		</>
	);
};

export default UpdateCard;
